import rulesData from "@/data/rules.json";
import type {
  CalendlyKey,
  DureeFrance,
  EvenementPasse,
  QualificationResult,
  RawRule,
  RulesFile,
  WizardAnswers,
} from "./types";
import { getPrefecture } from "./postal-to-prefecture";

const rules = rulesData as unknown as RulesFile;

const DUREE_ORDER: DureeFrance[] = rules.duree_france_ordering;

function dureeIndex(d: DureeFrance): number {
  return DUREE_ORDER.indexOf(d);
}

function gteDuree(actual: DureeFrance, threshold: DureeFrance): boolean {
  return dureeIndex(actual) >= dureeIndex(threshold);
}

function lteDuree(actual: DureeFrance, threshold: DureeFrance): boolean {
  return dureeIndex(actual) <= dureeIndex(threshold);
}

function hasRedFlag(events: EvenementPasse[]): boolean {
  const flags = rules.bypass.trigger.containsAny;
  return events.some((e) => flags.includes(e));
}

function matchesConditions(rule: RawRule, answers: WizardAnswers): boolean {
  const c = rule.conditions;

  for (const [key, expected] of Object.entries(c)) {
    switch (key) {
      case "besoin":
        if (answers.besoin !== expected) return false;
        break;
      case "nationalite":
        if (answers.nationalite !== expected) return false;
        break;
      case "situation":
        if (answers.situation !== expected) return false;
        break;
      case "profession":
        if (answers.profession !== expected) return false;
        break;
      case "profession_in":
        if (!(expected as string[]).includes(answers.profession)) return false;
        break;
      case "duree_france_min":
        if (!gteDuree(answers.duree_france, expected as DureeFrance)) return false;
        break;
      case "duree_france_max":
        if (!lteDuree(answers.duree_france, expected as DureeFrance)) return false;
        break;
      case "evenements_passes_contains_any":
        if (!answers.evenements_passes.some((e) => (expected as string[]).includes(e))) return false;
        break;
      case "free_text_contains_any": {
        const text = (answers.free_text || "").toLowerCase();
        if (!(expected as string[]).some((kw) => text.includes(kw.toLowerCase()))) return false;
        break;
      }
      default:
        // Unknown condition key — fail closed to avoid false positives.
        return false;
    }
  }

  return true;
}

function resolveCalendly(key: CalendlyKey | null): string | null {
  if (!key) return null;
  return rules.calendly_urls[key] ?? null;
}

function buildResult(
  rule: RawRule,
  answers: WizardAnswers,
  overrides?: { ai_contradiction?: boolean; ai_summary?: string },
): QualificationResult {
  const prefecture = getPrefecture(answers.contact.code_postal);

  return {
    rule_id: rule.id,
    result_case: rule.result_case,
    service: rule.service,
    ticket_estimate: rule.ticket_estimate,
    ticket_visible: rule.ticket_visible !== false,
    ticket_max: rule.ticket_max,
    ticket_post_audit_min: rule.ticket_post_audit_min,
    ticket_post_audit_max: rule.ticket_post_audit_max,
    ticket_note_key: rule.ticket_note_key,
    documents_required: rule.documents_required,
    calendly_url: resolveCalendly(rule.calendly_url_key),
    routing: rule.routing,
    mandatory_mention_key: rule.mandatory_mention_key,
    educational_message_key: rule.educational_message_key,
    urgency_message_key: rule.urgency_message_key,
    prefecture,
    upsell_mre: answers.nationalite === "MA" && rule.routing !== "hajji",
    ai_contradiction: overrides?.ai_contradiction ?? false,
    ai_summary: overrides?.ai_summary,
  };
}

export interface QualifyOptions {
  /** If the AI free-text analyzer detected a hidden OQTF/refus/condamnation, force Cas C. */
  forceContentieux?: boolean;
  ai_contradiction?: boolean;
  ai_summary?: string;
}

export function qualify(answers: WizardAnswers, opts: QualifyOptions = {}): QualificationResult {
  const contentieux = opts.forceContentieux || hasRedFlag(answers.evenements_passes);

  if (contentieux) {
    const bypassRule = rules.rules.find((r) => r.id === rules.bypass.rule_ref);
    if (!bypassRule) {
      throw new Error(`rules.json: bypass rule_ref "${rules.bypass.rule_ref}" not found`);
    }
    return buildResult(bypassRule, answers, {
      ai_contradiction: opts.ai_contradiction,
      ai_summary: opts.ai_summary,
    });
  }

  for (const rule of rules.rules) {
    if (rule.id === rules.bypass.rule_ref) continue;
    if (matchesConditions(rule, answers)) {
      return buildResult(rule, answers, {
        ai_contradiction: opts.ai_contradiction,
        ai_summary: opts.ai_summary,
      });
    }
  }

  // Fallback: case_14 (cadrage indeterminé)
  const fallback = rules.rules.find((r) => r.id === "case_14_indetermine");
  if (!fallback) {
    throw new Error("rules.json: case_14_indetermine fallback not found");
  }
  return buildResult(fallback, answers, {
    ai_contradiction: opts.ai_contradiction,
    ai_summary: opts.ai_summary,
  });
}
